import React, { useState, useEffect } from 'react';
import './index.css';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Container,
  Link,
  useTheme,
} from '@mui/material';
import Confetti from 'react-confetti';
import { useLocation, useParams } from 'react-router-dom';
import { useSettingsContext } from 'src/components/settings';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { RouterLink } from 'src/routes/components';
import { useResponsive } from 'src/hooks/use-responsive';
import { usePublicGetAllUsersQuery } from 'src/store/Reducer/settings';
import { useGetGiveawayDetailsByIdQuery } from 'src/store/Reducer/giveaway';
import { useLocales } from 'src/locales';


const visibleItemCount = 5;
const itemHeight = 80;
const centerOffset = Math.floor(visibleItemCount / 2);

const GiveawayResult = () => {

  const { t } = useLocales();
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const { id } = useParams();
  const { user } = useMockedUser();
  const { state } = useLocation();

  const { data } = usePublicGetAllUsersQuery({ id });

  let { data: giveawayDetail } = useGetGiveawayDetailsByIdQuery({ id });

  const participants = data?.data?.filter((item) => !state?.winners?.includes(item?._id)).map((item) => ({ name: item?.firstName + " " + item?.lastName })) || [];

  const currentUser = { name: user?.firstName };

  const isLightMode = theme.palette.mode === 'light';

  const baseList = [...participants, currentUser];

  const displayList =
    baseList.length <= 3
      ? Array(Math.ceil(6 / baseList.length))
        .fill(baseList)
        .flat()
        .slice(0, 6)
      : baseList;



  const settings = useSettingsContext();

  const isDrawing = useBoolean();
  const openDialog = useBoolean();
  const showCelebration = useBoolean();

  const [countdown, setCountdown] = useState(5);
  const [winner, setWinner] = useState(null);
  const [slotPosition, setSlotPosition] = useState(itemHeight * centerOffset);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (isDrawing.value && countdown > 0) {
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
    if (countdown === 0) {
      isDrawing.onFalse();
      startSlotAnimation();
    }
  }, [countdown, isDrawing.value]);

  const startSlotAnimation = () => {
    const participantsWithUser = [...baseList];
    const winnerIndex = Math.floor(Math.random() * participantsWithUser.length);
    const winner = participantsWithUser[winnerIndex];

    setWinner(winner.name);
    let currentPosition = 0;
    const spinInterval = setInterval(() => {
      currentPosition += itemHeight;
      setSlotPosition(currentPosition);

      if (currentPosition >= participantsWithUser.length * itemHeight) {
        currentPosition = 0;
        setSlotPosition(currentPosition);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(spinInterval);

      const targetPosition = (winnerIndex - centerOffset) * itemHeight;
      const safeTargetPosition = Math.max(0, targetPosition);

      setSlotPosition(safeTargetPosition);

      setTimeout(() => {
        announceWinner(winner);
      }, 500);
    }, 5000);
  };
  const announceWinner = (winner) => {
    openDialog.onTrue();
  };

  const startDrawing = () => {
    isDrawing.onTrue();
    setCountdown(5);
  };

  useEffect(() => {
    if (openDialog.value) {
      showCelebration.onTrue();
      setTimeout(() => {
        showCelebration.onFalse();
      }, 10000);
    }
  }, [openDialog.value]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetDrawing = () => {
    isDrawing.onFalse();
    showCelebration.onFalse();
    setCountdown(5);
    setWinner(null);
    setSlotPosition(0);
  };

  return (
    <Container
      maxWidth={settings.themeStretch ? false : 'xl'}
      sx={{
        position: 'relative',
        minHeight: '85vh',
        overflow: 'hidden',
      }}
    // sx={{
    //   backgroundImage: 'url(/assets/images/backgroundImage.png)',
    //   backgroundSize: 'contain',
    //   backgroundPosition: 'center',
    //   backgroundRepeat: 'no-repeat',
    //   minHeight: '80vh',
    //   width: 1,
    // }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(/assets/images/${isLightMode ? 'logoLightMode.png' : 'backgroundImage.png'
            })`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: isLightMode ? 0.1 : 1,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <header>
        <div className="logo">
          <Box
            component={'img'}
            // src="/assets/images/giftImage.png"
            src={giveawayDetail?.data?.basicInfo?.image || "/assets/images/giftImage.png"}
            sx={{
              mt: 2,
              width: { xs: 150, md: 300 },
              height: { xs: 150, md: 300 },
              objectFit: 'cover',
              borderRadius: 2,
            }}
          />
        </div>

        <Typography
          fontSize={{
            xs: '1.5rem',
            md: '2rem',
            lg: '2.5rem',
            textDecoration: 'none',
            color: theme.palette.mode === 'light' ? 'black' : 'white',
          }}

          fontWeight="bold"
          component={RouterLink}
          href="http://luckyads.app"
          target="_blank"
        >
          {/* Sponsored by <span >GameZone</span> */}
          {giveawayDetail?.data?.basicInfo?.title || t("home.giveaway_live_draw")}
        </Typography>
      </header>
      <Box
        sx={{
          width: { xs: 1, md: 0.3 },
          margin: '0 auto',
          border: '2px solid #5ebe89',
          borderRadius: '16px',
          boxShadow: '0 0 20px #5ebe89',
          overflow: 'hidden',
        }}
      >
        <div
          className="slot-machine"
          style={{ height: itemHeight * visibleItemCount, overflow: 'hidden' }}
        >
          <div className="slot-container">
            <div className="slot-window">
              {

                displayList.map((participant, index) => {
                  const currentCenteredIndex = Math.round(slotPosition / itemHeight) + centerOffset;
                  const distanceFromCenter = Math.abs(index - currentCenteredIndex);

                  let itemClass = 'slot-item';
                  if (distanceFromCenter === 0) itemClass += ' highlight';
                  else if (distanceFromCenter === 1) itemClass += ' near-center';
                  else itemClass += ' outer';
                  return (
                    <div
                      className={itemClass}
                      key={index}
                      style={{
                        height: `${itemHeight}px`,
                        transform: `translateY(${index * itemHeight - slotPosition}px)`,
                        transition: 'transform 0.2s ease-in-out',
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <div className="participant-info">
                        <div className="participant-name">{participant.name}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          {isDrawing.value && (
            <div className="countdown-container">
              <div className="countdown-circle"></div>
              <div className="countdown-number">{countdown}</div>
            </div>
          )}
        </div>
      </Box>

      <div className="controls">
        <button className="btn btn-primary" onClick={startDrawing} disabled={user?.userType === "super-admin" || user?.userType === "organizer"}>
          <Iconify icon="solar:play-bold" />
          {t("home.start_drawing")}
        </button>
        {/* <button className="btn btn-secondary" sx={{color:isLightMode ? 'black' : 'white'}} onClick={resetDrawing}>
          <Iconify icon="mdi:refresh" />
          Reset
        </button> */}

        <Button
          variant="outlined"
          onClick={resetDrawing}
          sx={{
            padding: '1rem 2rem',
            borderRadius: '25px',
            fontSize: '1.1rem',
            fontWeight: 600,
            gap: '0.5rem',
            borderColor: 'primary.main',
            color: isLightMode ? 'black' : 'white',
            backgroundColor: 'transparent',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#00ff9d',
              color: 'background.paper',
              transform: 'translateY(-2px)',
              // boxShadow: (theme) => `0 5px 15px ${theme.palette.primary.main}`,
            },
          }}
        >
          <Iconify icon="mdi:refresh" />
          {t("home.reset")}
        </Button>

      </div>
      <Box
        sx={{ width: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}
      >
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
          {t("home.powered_by")}{" "}
          <Link
            component={RouterLink}
            href="https://chain.link/"
            target="_blank"
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              textDecoration: 'none',
            }}
          >
            {' '}
            ChainLink{' '}
            <Iconify icon="simple-icons:chainlink" width={24} sx={{ color: 'blue', ml: 1 }} />
          </Link>
        </Typography>
      </Box>
      <Dialog open={openDialog.value} onClose={() => openDialog.onFalse()} maxWidth="xs" fullWidth PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              border: 'none'
            },
          }}>
        <Box
          // sx={{
          //   backgroundImage: 'url(/assets/images/modalBackgroundImage.png)',
          //   backgroundSize: 'cover',
          //   backgroundPosition: 'center',
          //   backgroundRepeat: 'no-repeat',
          //   boxShadow: 24,
          //   borderRadius: 2,
          //   width: 1,
          //   height: 1,
          // }}
          sx={{
            backgroundImage: 'url(/assets/images/modalBackgroundImage.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            boxShadow: 24,
            borderRadius: 2,
            width: 1,
            height: 1,
            backgroundColor: "transparent"
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            <Box
              component="img"
              src="/assets/images/winnerImage.png"
              sx={{ width: { xs: 100, md: 150 }, height: { xs: 100, md: 150 } }}
            />
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              sx={{
                textAlign: 'center',
                textShadow: '1px 1px 4px rgba(0,0,0,0.3)',
                color: 'white',
                fontWeight: 'bold',
                wordWrap: 'break-word',
              }}
            >
              Congratulations
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3" component="h3"
                color={'primary'}
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  textShadow: (theme) => `1px 1px solid 4px ${theme.palette.primary.main}`,
                  textAlign: 'center',
                }}
              >
                {winner}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, paddingBottom: 3, color: 'white', textAlign: 'center' }}>
                You are the lucky Winner!
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box
              fullWidth
              width={1}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  width: 1,
                  backgroundColor: 'primary.main',
                  color: 'black',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                }}
                onClick={openDialog.onFalse}
              >
                Continue
              </Button>
            </Box>
          </DialogActions>
        </Box>
      </Dialog>

      {showCelebration.value && (
        <Confetti width={windowSize.width} height={windowSize.height} numberOfPieces={400} />
      )}
    </Container>
  );
};

export default GiveawayResult;
